import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { PAYMENT_GATE_LABEL, errorMessage } from "../lib/rentalLifecycle";

// Task 7: the business's own release and settlement policy, plus the
// operational queues and the read-only integrity sweep.
//
// Nothing here is a legal requirement invented by ConfirmPH. Out of the box
// no money rule blocks a release, and the deposit refund target is the
// business's own promise to its customers.
const QUEUES = [
  ["active_rentals", "Active rentals"],
  ["returns_due", "Returns due"],
  ["late_returns", "Late returns"],
  ["needs_inspection", "Needs inspection"],
  ["open_damage_cases", "Open damage cases"],
  ["open_claims", "Open insurance claims"],
  ["deposits_held", "Deposits held"],
  ["deposit_refunds_due", "Refunds due"],
  ["open_customer_balances", "Open customer balances"],
  ["open_disputes", "Open disputes"],
  ["post_rental_reconciliation", "Post-rental reconciliation"],
];

export default function RentalSettlementPolicy() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [denied, setDenied] = useState(false);
  const [queue, setQueue] = useState({ name: "active_rentals", rows: null });
  const [control, setControl] = useState(null);
  const [sweep, setSweep] = useState(null);

  const load = useCallback(() => {
    api.get("/rental-lifecycle/policy").then((r) => {
      setData(r.data);
      setForm({ ...r.data.policy });
    }).catch((err) => {
      if (err?.response?.status === 403) setDenied(true); else toast.error(errorMessage(err));
    });
    api.get("/rental-lifecycle/reports/financial-control")
      .then((r) => setControl(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/rental-lifecycle/reports/${queue.name}`)
      .then((r) => setQueue((q) => ({ ...q, rows: r.data })))
      .catch(() => setQueue((q) => ({ ...q, rows: { items: [] } })));
  }, [queue.name]);

  if (denied) {
    return (
      <div className="rounded-xl border bg-white p-6" data-testid="rental-policy-denied">
        You don&apos;t have access to rental settlement settings.
      </div>
    );
  }
  if (!data || !form) return <div className="text-slate-500">Loading…</div>;
  const canEdit = (data.capabilities || []).includes("rental.policy_configure");

  const save = async () => {
    try {
      await api.put("/rental-lifecycle/policy", {
        payment_gate: form.payment_gate,
        require_advance_before_release: form.require_advance_before_release,
        require_deposit_before_release: form.require_deposit_before_release,
        require_signed_agreement_before_release: form.require_signed_agreement_before_release,
        require_pickup_condition_before_release: form.require_pickup_condition_before_release,
        return_inspection_required_before_close: form.return_inspection_required_before_close,
        deposit_refund_target_days: form.deposit_refund_target_days === "" ? null
          : Number(form.deposit_refund_target_days),
        late_return_grace_minutes: Number(form.late_return_grace_minutes || 0),
      });
      toast.success("Policy saved");
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const check = (key, label, help) => (
    <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" checked={!!form[key]} disabled={!canEdit}
             onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
             data-testid={`rental-policy-${key}`} />
      <span>{label}{help && <span className="block text-xs text-slate-500">{help}</span>}</span>
    </label>
  );

  return (
    <div className="space-y-6" data-testid="rental-settlement-policy">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3">
        <h1 className="text-lg font-semibold">Release and settlement policy</h1>
        <p className="text-sm text-slate-600">
          These are your business&apos;s rules. ConfirmPH does not require payment, an advance or a
          deposit before a release — you decide.
        </p>

        <label className="block text-sm">
          <span className="font-medium">Before the vehicle is released</span>
          <select className="mt-1 w-full rounded border border-slate-300 p-2 text-sm"
                  value={form.payment_gate} disabled={!canEdit}
                  data-testid="rental-policy-gate"
                  onChange={(e) => setForm({ ...form, payment_gate: e.target.value })}>
            {Object.entries(PAYMENT_GATE_LABEL).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </label>

        {check("require_advance_before_release", "The agreed advance must be recorded")}
        {check("require_deposit_before_release", "The agreed security deposit must be recorded")}
        {check("require_signed_agreement_before_release",
               "The booking must sit on published terms the customer accepted")}
        {check("require_pickup_condition_before_release", "A pickup condition record must exist")}
        {check("return_inspection_required_before_close",
               "A finished return inspection is needed before a rental can close")}

        <label className="block text-sm">
          <span className="font-medium">Deposit refund target (days)</span>
          <span className="block text-xs text-slate-500">
            Your own promise to customers. ConfirmPH does not set a Philippine legal deadline.
          </span>
          <input type="number" min="0" className="mt-1 w-32 rounded border border-slate-300 p-2 text-sm"
                 value={form.deposit_refund_target_days ?? ""} disabled={!canEdit}
                 data-testid="rental-policy-refund-days"
                 onChange={(e) => setForm({ ...form, deposit_refund_target_days: e.target.value })} />
        </label>

        <label className="block text-sm">
          <span className="font-medium">Late return grace period (minutes)</span>
          <input type="number" min="0" className="mt-1 w-32 rounded border border-slate-300 p-2 text-sm"
                 value={form.late_return_grace_minutes ?? 0} disabled={!canEdit}
                 onChange={(e) => setForm({ ...form, late_return_grace_minutes: e.target.value })} />
        </label>

        {canEdit && (
          <button type="button" onClick={save} data-testid="rental-policy-save"
                  className="rounded-lg border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white">
            Save policy
          </button>
        )}

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
          <h2 className="font-medium">What your customers are told</h2>
          <ul className="mt-1 space-y-0.5">
            {data.customer_disclosure.map((d) => (
              <li key={d.key}>{d.text} <span className="text-xs text-slate-500">— Rental business policy</span></li>
            ))}
          </ul>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3">
        <h2 className="font-semibold">What needs attention</h2>
        <div className="flex flex-wrap gap-2">
          {QUEUES.map(([key, label]) => (
            <button key={key} type="button"
                    onClick={() => setQueue({ name: key, rows: null })}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      queue.name === key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"}`}
                    data-testid={`rental-queue-${key}`}>
              {label}
            </button>
          ))}
        </div>
        <ul className="space-y-1 text-sm" data-testid="rental-queue-rows">
          {(queue.rows?.items || []).map((row, i) => (
            <li key={row.booking_id || row.id || i} className="flex flex-wrap justify-between gap-2">
              <span>{row.booking_reference || row.name || row.description || row.id}</span>
              <span className="text-xs text-slate-600">
                {row.customer_name || row.plate_number || row.status || ""}
              </span>
            </li>
          ))}
          {queue.rows && queue.rows.items.length === 0 && (
            <li className="text-slate-600">Nothing here.</li>
          )}
        </ul>
      </section>

      {control && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-2">
          <h2 className="font-semibold">Money control checks</h2>
          {control.clean && <p className="text-sm text-slate-600">Nothing to look at.</p>}
          <ul className="space-y-1 text-sm" data-testid="rental-control-flags">
            {control.flags.map((f) => (
              <li key={f.code}>{f.message} <span className="text-xs text-slate-500">({f.count})</span></li>
            ))}
          </ul>
          <button type="button" className="text-xs underline" data-testid="rental-sweep-button"
                  onClick={() => api.get("/rental-lifecycle/integrity-sweep")
                    .then((r) => setSweep(r.data))
                    .catch((err) => toast.error(errorMessage(err)))}>
            Run the read-only integrity check
          </button>
          {sweep && (
            <div className="text-sm" data-testid="rental-sweep-result">
              <p>{sweep.finding_count} finding{sweep.finding_count === 1 ? "" : "s"}. This check only
                 reports; it never changes anything.</p>
              <ul className="mt-1 space-y-0.5">
                {sweep.findings.slice(0, 20).map((f, i) => (
                  <li key={`${f.code}-${i}`}>{f.message}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
